/**
 * ProjectBatchStatusList — Day Diary v2 per-batch progress table (task 237, D2).
 *
 * Rendered while the diary's `status != 'complete'` (AC1). Polling-driven —
 * the parent (DayDiaryPage via `useDayDiary`) re-fetches GET `/day-diary-v2`
 * and feeds the fresh `batches` array; this component is presentational.
 *
 * One row per `DayDiaryProjectBatch`: the project (or "Reclamation" for the
 * fallback bucket where `projectId === null`), the batch status, and the
 * event count.
 */

import type { DiaryProjectBatchStatus } from '@/types/dayDiaryV2';
import styles from './ProjectBatchStatusList.module.css';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Queued',
  fanned_out: 'Queued',
  in_progress: 'Working',
  consolidating: 'Summarizing',
  complete: 'Done',
  failed: 'Needs attention',
};

export interface ProjectBatchStatusListProps {
  batches: DiaryProjectBatchStatus[];
}

export function ProjectBatchStatusList({ batches }: ProjectBatchStatusListProps) {
  return (
    <table className={styles.table} data-testid="project-batch-status-list">
      <thead>
        <tr>
          <th>Project</th>
          <th>Status</th>
          <th className={styles.numCol}>Events</th>
        </tr>
      </thead>
      <tbody>
        {batches.map((b) => (
          <tr key={b.id} data-testid="project-batch-row">
            <td>{batchLabel(b)}</td>
            <td>
              <span
                className={`${styles.statusPill} ${styles[`status_${b.status}`] ?? ''}`}
                data-testid="project-batch-status"
              >
                {STATUS_LABEL[b.status] ?? b.status}
              </span>
            </td>
            <td className={styles.numCol}>{b.eventCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function batchLabel(b: DiaryProjectBatchStatus): string {
  if (b.projectId === null) return 'Reclamation';
  const project = b.projectName ?? 'Project';
  return b.clientName ? `${b.clientName} / ${project}` : project;
}
